import React, { useEffect, useRef, useState } from "react"
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import PlusIcon from "../../images/PlusIcon";
import CloseIcon from "../../images/CloseIcon";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import { deleteFloorPlan, getListFloorPlan, updateFloorPlanStatus } from "../../services/floorPlan.service";
import { useResponsive } from "../../hook/useResponsive";
import FLoorPlanIcon from "../../images/FLoorPlanIcon";
import { showApiError } from "../../services/apiError";
import { ConfirmSheet } from "../../components/mobile";
import {
    ListColumn,
    ListQuery,
    ListRowAction,
    ListScreen,
    ListScreenHandle,
    cells,
    t,
} from "../../components/list";

interface FloorPlanRow {
    id: string;
    planName: string;
    position: number;
    description: string | null;
    isActive: boolean;
}

const ListFloorPlan = () => {
    const preLanguage = localStorage.getItem("language");
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<ListScreenHandle>(null);
    const [language, setLanguage] = useState<any>(null);
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [idDelete, setIdDelete] = useState<string>("")
    // Switch values shown while a status save is in flight.
    const [statusOverrides, setStatusOverrides] = useState<Record<string, boolean>>({});
    const statusSaving = useRef(new Set<string>());
    const { isTouchLayout } = useResponsive();
    const inforTable: any = {
        title: language && language?.["No floor plans yet"] ? language?.["No floor plans yet"] : "No floor plans yet", desc: language && language?.[`Create and manage Floor Plans (e.g., Restaurant, Bar, Terrace) to set 
location-specific menus, pricing, and revenue reports.`] ? language?.[`Create and manage Floor Plans (e.g., Restaurant, Bar, Terrace) to set 
location-specific menus, pricing, and revenue reports.`] : `Create and manage Floor Plans (e.g., Restaurant, Bar, Terrace) to set 
location-specific menus, pricing, and revenue reports.`,
        img: <FLoorPlanIcon />
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            event.preventDefault();
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };

    }, []);
    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);

    /** One page of the current location's floor plans; the search box maps onto `searchValue` (plan name). */
    const fetchFloorPlans = async (query: ListQuery) => {
        webLoadingFalse();
        // Always send a string: the API reads a missing searchValue as a search for "undefined".
        const response: any = await getListFloorPlan(query.page, query.limit, String(query.search ?? "").trim(), (localStorage.getItem("locationId") || ""));
        return {
            rows: response?.data?.data?.data ?? [],
            count: response?.data?.data?.count ?? 0,
        };
    };

    const goToFloorPlan = (id: string) => webNavigate('/' + preLanguage + '/floorPlan/' + id);

    const handleShowDelete = (id: string) => {
        setShowDelete(true);
        setIdDelete(id)
    }
    const handleDeleteEmployee = async () => {
        try {
            await deleteFloorPlan(idDelete);
        } catch (error) {
            showApiError(error, { language });
        } finally {
            listRef.current?.reload();
            setShowDelete(false)
        }
    }

    const handleUpdateStatus = async (row: FloorPlanRow) => {
        const id = String(row.id);
        if (statusSaving.current.has(id)) return;
        statusSaving.current.add(id);
        setStatusOverrides(prev => ({ ...prev, [id]: !row.isActive }));
        try {
            await updateFloorPlanStatus(id, !row.isActive);
            await listRef.current?.reload();
        } catch (error) {
            showApiError(error, { language });
        } finally {
            // Dropping the override reverts the switch on failure, or hands over to the reloaded row.
            statusSaving.current.delete(id);
            setStatusOverrides(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
        }
    }

    /* ---------------- List kit configuration ---------------- */

    const columns: ListColumn<FloorPlanRow>[] = [
        {
            key: "planName",
            label: "Floor Plan",
            width: 260,
            hideable: false,
            render: cells.primary(p => p.planName),
            mobile: "title",
            onClick: p => goToFloorPlan(String(p.id)),
        },
        {
            key: "position",
            label: "Position",
            width: 100,
            value: p => p.position,
            mobile: "sheet",
        },
        {
            key: "description",
            label: "Description",
            width: 320,
            value: p => p.description || "",
            render: cells.text(p => p.description || "/"),
            mobile: "subtitle",
        },
        {
            key: "status",
            label: "Status",
            width: 120,
            render: cells.toggle(p => statusOverrides[String(p.id)] ?? p.isActive, handleUpdateStatus),
            mobile: "trailing",
        },
    ];

    const rowActions: ListRowAction<FloorPlanRow>[] = [
        {
            key: "edit",
            label: "Edit",
            icon: <EditIconV2 />,
            sheetIcon: "inventory",
            onRun: p => goToFloorPlan(String(p.id)),
        },
        {
            key: "delete",
            label: "Delete",
            icon: <DeleteIconV2 />,
            sheetIcon: "close",
            danger: true,
            onRun: p => handleShowDelete(String(p.id)),
        },
    ];

    return (
        <>
            <div
                className="h-full relative"
                style={{
                    overflowY: isTouchLayout ? "auto" : undefined,
                    paddingBottom: isTouchLayout ? "24px" : undefined,
                    display: isTouchLayout ? undefined : "flex",
                    flexDirection: isTouchLayout ? undefined : "column",
                }}
            >
                <div className="lg:flex items-center justify-between">
                    <p
                        style={{ color: '#222525' }}
                        className="text-[24px] leading-[130%] font-medium font-inter"
                    >
                        {language && language?.['Floor Plans'] ? language?.['Floor Plans'] : 'Floor Plans'}
                    </p>
                    <div
                        className="cursor-pointer flex items-center justify-center"
                        style={{
                            gap: '8px',
                            padding: "8px 24px 8px 16px",
                            backgroundColor: '#0F7679',
                            borderRadius: '30px',
                            width: "fit-content", marginTop: isTouchLayout ? "20px" : '0px',
                        }}
                        onClick={() => {
                            webNavigate('/' + preLanguage + '/floorPlan/new');
                        }}
                    >
                        <div className="w-6 h-6 flex items-center justify-center">
                            <PlusIcon />
                        </div>
                        <p
                            style={{ color: '#FFFFFF' }}
                            className="text-[14px] leading-[130%] font-medium font-inter"
                        >
                            {language && language?.['Add floor plan'] ? language?.['Add floor plan'] : 'Add floor plan'}
                        </p>
                    </div>
                </div>
                <ListScreen
                    ref={listRef}
                    language={language}
                    columns={columns}
                    fetchRows={fetchFloorPlans}
                    searchPlaceholder="Search floor plans"
                    searchInputRef={searchInputRef}
                    rowActions={rowActions}
                    countNoun="floor plans"
                    emptyState={inforTable}
                />
            </div>
            {isTouchLayout ? (
                <ConfirmSheet
                    open={showDelete}
                    title={t(language, "Delete Floor Plan?", "Delete Floor Plan?")}
                    message={t(language, "You will permanently delete the Floor Plan", "You will permanently delete the Floor Plan")}
                    confirmLabel={t(language, "Delete", "Delete")}
                    cancelLabel={t(language, "Cancel", "Cancel")}
                    onConfirm={handleDeleteEmployee}
                    onCancel={() => setShowDelete(false)}
                />
            ) : (
                <div
                    className={`fixed w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                        } transition-opacity duration-300 ease-in-out`}
                    style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100%" }}
                >
                    <div
                        className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`} style={{
                            padding: "24px", borderRadius: "24px", backgroundColor: "#FFFFFF", width: "530px", maxWidth: "calc(100vw - 32px)", transition: "transform 0.3s ease, opacity 0.3s ease",
                        }}
                    >
                        <div
                            className="flex items-center justify-between"
                        >
                            <p className="text-[16px] font-semibold font-inter text-main leading-[24px]">
                                {language && language?.["Delete Floor Plan?"] ? language?.["Delete Floor Plan?"] : "Delete Floor Plan?"}
                            </p>
                            <div
                                onClick={() => setShowDelete(false)}
                                className="flex items-center justify-center cursor-pointer"
                                style={{
                                    width: "24px",
                                    height: "24px",
                                }}
                            >
                                <CloseIcon />
                            </div>
                        </div>
                        <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
                            <p className="text-[14px] font-inter leading-[24px]" style={{ color: '#6A7878' }}>
                                {language && language?.["You will permanently delete the Floor Plan"] ? language?.["You will permanently delete the Floor Plan"] : "You will permanently delete the Floor Plan"}
                            </p>
                        </div>
                        <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
                            <div
                                onClick={() => setShowDelete(false)}
                                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                                style={{
                                    borderRadius: '30px',
                                    padding: '10px 24px',
                                    border: '1px solid #C9D7D8',
                                    color: '#222525',
                                }}
                            >
                                {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                            </div>
                            <div
                                onClick={handleDeleteEmployee}
                                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                                style={{
                                    borderRadius: '30px',
                                    padding: '10px 24px',
                                    backgroundColor: '#BA0E13',
                                    color: '#FFFFFF',
                                }}
                            >
                                {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
};

export default ListFloorPlan;
